import React from 'react'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {ShieldCheck, Palette, Layout, Smartphone, CheckCircle, Download} from 'lucide-react'

export default function ElementorPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            <span>Official Licensed Plugin</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Elementor WHMCS Client Area Designer
          </h1>
          
          <p className="text-xl text-slate-600 mb-8">
            Transform your WHMCS client areas with Elementor's powerful drag-and-drop page builder. Create stunning, professional designs without coding.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Purchase Now - $79
            </button>
            <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all">
              View Demo
            </button>
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
            <Download className="w-4 h-4" />
            <span>Instant download • Free lifetime updates</span>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Design Freedom
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Drag & Drop Builder</h3>
              <p className="text-slate-600">
                Visual editor with real-time preview. Build beautiful client areas without touching code.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Layout className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Pre-built Templates</h3>
              <p className="text-slate-600">
                Start with professionally designed templates and customize to match your brand perfectly.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Fully Responsive</h3>
              <p className="text-slate-600">
                Designs automatically adapt to all devices. Control mobile, tablet, and desktop layouts separately.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">WHMCS Integration</h3>
              <p className="text-slate-600">
                Seamless integration with WHMCS elements. Style invoices, tickets, services, and more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Style */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Customize Every Page
          </h2>

          <div className="space-y-4">
            {[
              'Client Dashboard - Create stunning overview pages with custom widgets',
              'Invoice Pages - Design professional invoice layouts with your branding',
              'Support Ticket Interface - Build modern ticket management interfaces',
              'Service Management - Style product and service pages for better UX',
              'Shopping Cart - Design conversion-optimized checkout experiences',
              'Login & Registration - Create branded authentication pages'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Why Elementor for WHMCS?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">No Coding Required</h3>
              <p className="text-slate-600">
                Design professional client areas without HTML, CSS, or PHP knowledge. Visual editor makes it simple.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Stand Out from Competition</h3>
              <p className="text-slate-600">
                Generic WHMCS themes make businesses look the same. Create unique designs that reflect your brand.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Improve Conversions</h3>
              <p className="text-slate-600">
                Better design leads to better results. Optimize layouts for user experience and increase sales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your WHMCS Client Area?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start creating beautiful, professional designs with Elementor today.
          </p>
          <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
            Purchase Now - $79
          </button>
        </div>
      </section>

      <Footer />
    </div>
  )
}
